import React, { useState, useEffect } from "react";
import { Box, IconButton, useTheme } from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import PortalHeader from "./PortalHeader";
import PortalSidebar from "./PortalSidebar";
import PortalPersistentSidebar from "./PortalPersistentSidebar";
import PortalFooter from "./PortalFooter";
import { loadClientCSS, unloadClientCSS } from "../../services/client-assets";

interface PortalLayoutProps {
  children: React.ReactNode;
  clientData: {
    subdomain: string;
    clientName: string;
  };
  onLogout: () => void;
}

const PortalLayout: React.FC<PortalLayoutProps> = ({ children, clientData, onLogout }) => {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerWidth] = useState(280);

  // Load client CSS when component mounts
  useEffect(() => {
    const loadCSS = async () => {
      try {
        await loadClientCSS(clientData.subdomain);
      } catch (error) {
        console.error("Failed to load client CSS:", error);
      }
    };

    loadCSS();

    // Cleanup on unmount
    return () => {
      unloadClientCSS();
    };
  }, [clientData.subdomain]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Header */}
      <PortalHeader clientData={clientData} onLogout={onLogout} />

      {/* Mobile Menu Button */}
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          position: "fixed",
          top: 80,
          left: 16,
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            "&:hover": {
              bgcolor: theme.palette.action.hover,
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ display: "flex", flex: 1 }}>
        {/* Desktop Sidebar */}
        <PortalPersistentSidebar drawerWidth={drawerWidth} />

        {/* Mobile Sidebar */}
        <PortalSidebar drawerWidth={drawerWidth} open={mobileOpen} onClose={handleDrawerToggle} />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            bgcolor: theme.palette.background.default,
            p: { xs: 2, sm: 3, md: 4 },
            pt: { xs: 8, sm: 3, md: 4 }, // Extra top padding on mobile for menu button
            minHeight: "calc(100vh - 64px)", // Full height minus header
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Footer */}
      <PortalFooter clientData={clientData} />
    </Box>
  );
};

export default PortalLayout;
